import type { MutableRefObject } from 'react';
import type { JanusJS } from 'janus-gateway';

type MonitorHandle = {
  detach?: () => void;
  hangup?: () => void;
} | null;

type MonitorJanus = {
  destroy?: (callbacks: JanusJS.DestroyOptions) => void;
} | null;

export interface MonitorTransportRefs {
  janusPluginRef: MutableRefObject<string | null>;
  janusRef: MutableRefObject<MonitorJanus>;
  pcDebugRef: MutableRefObject<RTCPeerConnection | null>;
  remoteStreamRef: MutableRefObject<MediaStream | null>;
  streamingHandleRef: MutableRefObject<MonitorHandle>;
}

export interface TeardownMonitorTransportOptions {
  clearMonitorAudioStream: () => void;
  refs: MonitorTransportRefs;
  skipHangup?: boolean;
}

/**
 * Teardown the Janus monitor transport in an idempotent order.
 *
 * The monitor plugin fires `oncleanup` when `hangup()`/`destroy()` runs.
 * To avoid re-entering the same teardown path recursively, this helper
 * nulls every live ref before calling any Janus side effect.
 */
export function teardownMonitorTransport({
  clearMonitorAudioStream,
  refs,
  skipHangup = false,
}: TeardownMonitorTransportOptions): void {
  const streamingHandle = refs.streamingHandleRef.current;
  const janus = refs.janusRef.current;

  refs.streamingHandleRef.current = null;
  refs.janusRef.current = null;
  refs.janusPluginRef.current = null;
  refs.remoteStreamRef.current = null;
  refs.pcDebugRef.current = null;
  clearMonitorAudioStream();

  if (streamingHandle) {
    if (!skipHangup && typeof streamingHandle.hangup === 'function') {
      try {
        streamingHandle.hangup();
      } catch {
        // no-op
      }
    }
    if (typeof streamingHandle.detach === 'function') {
      try {
        streamingHandle.detach();
      } catch {
        // no-op
      }
    }
  }

  if (janus && typeof janus.destroy === 'function') {
    try {
      janus.destroy({});
    } catch {
      // no-op
    }
  }
}
